import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ClickableUploadIcon, ErrorResponse, Toolbar } from '~components';
import Field from '~components/form-fields/Field';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faFileImport, faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view-v3';
import AddModal from '~support-at-home/components/common/AddModal';

import { SahClientServicesGrouped } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/client-services/listAll';
import searchFields from '~support-at-home/forms/client-services/search';

import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { getAll } from '~main/actions/clients';
import { create, getGrouped, uploadFile } from '~support-at-home/actions/clientServices';

import { clear, clearError } from '~support-at-home/reducers/clientServices';

import { selectSahCategoryById } from '~lookups/selectors/sahCategories';
import { selectLookupClients } from '~main/selectors/clients/clients';
import {
  selectErrorResponse,
  selectSahClientServicesGrouped,
  selectUploadError,
} from '~support-at-home/selectors/clientServices';

import ServicesModal from './ServicesModal';

const Services: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isClientOpen, setClientOpen] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isAddModalOpen, setAddModalOpen] = useState<boolean>(false);
  interface NewService {
    client_id: string;
    service_id: string;
    start_date: string;
    end_date?: string | null;
  }
  const [newService, setNewService] = useState<NewService>({
    client_id: '',
    service_id: '',
    start_date: '',
    end_date: null,
  });
  const { data: services, isLoading } = useGetSahServicesQuery();

  const groupedServices = useAppSelector(selectSahClientServicesGrouped);
  const errorResponse = useAppSelector(selectErrorResponse);
  const uploadError = useAppSelector(selectUploadError);

  const [data, setData] = useState<SahClientServicesGrouped[]>(groupedServices);

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('sah-client-services-search', {});
  const search = (values: { [key: string]: any }) => setSearchParams(values);

  const categoryId = services?.find((s) => s.id === newService.service_id)?.category_id ?? null;
  const category = useAppSelector((state) => (categoryId ? selectSahCategoryById(state, categoryId) : null));
  const toggleModal = (toggle: boolean, clientId: number | null = null) => {
    setSelectedClientId(clientId);
    setClientOpen(toggle);
    load();
  };

  const openAddModal = () => {
    setNewService({ client_id: '', service_id: '', start_date: '', end_date: null });
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const handleAddServiceChange = (name: string, value: any) => {
    setNewService((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddServiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { end_date, ...rest } = newService;
    const payload = end_date ? { ...rest, end_date } : { ...rest };
    await dispatch(create(payload)).unwrap();
    await load();
    setAddModalOpen(false);
  };

  const onModalClose = () => {
    toggleModal(false);
  };

  const load = async () => {
    dispatch(clearError([]));
    dispatch(getGrouped({ ...searchParams }));
  };

  const onUpload = async (file: File, setProgress: (value: number) => void) => {
    await dispatch(uploadFile({ file, setProgress })).unwrap();
    await load();
  };

  useEffect(() => {
    load();
    return () => {
      dispatch(clear([]));
    };
  }, []);

  useEffect(() => {
    load();
  }, [searchParams]);

  useEffect(() => {
    setData(groupedServices as SahClientServicesGrouped[]);
  }, [groupedServices]);

  const columns = columnsFactory((id: number) => toggleModal(true, id));

  const isAddServiceValid = !!newService.client_id && !!newService.service_id && !!newService.start_date;

  return (
    <div className="p-2">
      <ErrorResponse message={errorResponse} dataCy="sah-client-services-error" />
      <Toolbar
        id="sah-services"
        rightCluster={
          <>
            <ClickableUploadIcon
              id="sah-client-services-file-upload-button"
              icon={faFileImport}
              toolTip="Import CSV / JSON file"
              onUpload={onUpload}
              externalError={uploadError}
            />
            <ClickableIcon
              id="sah-client-services-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Search"
            />
            <Button id="sah-client-services-add-button" size="sm" color="success" onClick={openAddModal}>
              <Icon className="me-2" icon={faPlus} />
              Add Service
            </Button>
          </>
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} onSubmit={search} />
      <TableView
        id="sah-client-services-grouped-table"
        columns={columns}
        data={data}
        emptyMessage={'No client SAH services found'}
        isLoading={isLoading}
        readonly
      />
      <ServicesModal id={selectedClientId} isOpen={isClientOpen} onClose={onModalClose} />

      <AddModal
        isOpen={isAddModalOpen}
        title="Add New SaH Service"
        onClose={closeAddModal}
        onSubmit={handleAddServiceSubmit}
        submitLabel="Add Service"
        submitDisabled={!isAddServiceValid}
      >
        <Field
          id="client_id"
          caption="Client"
          type="select-lazy"
          value={newService.client_id}
          selectorActions={getAll}
          selectorOptions={selectLookupClients}
          required
          fieldId="client_id"
          handleChange={handleAddServiceChange}
        />
        <Field
          id="service_id"
          caption="Service Name"
          type="select"
          value={newService.service_id}
          options={services?.map((s: any) => ({ id: s.id, name: s.name }))}
          handleChange={handleAddServiceChange}
          fieldId="service_id"
          required
        />
        <Field
          id="category_id"
          caption="Category"
          type="readonly"
          value={category?.name}
          handleChange={handleAddServiceChange}
          fieldId="category_id"
        />
        <Field
          id="start_date"
          caption="Start Date"
          type="date"
          value={newService.start_date}
          handleChange={handleAddServiceChange}
          fieldId="start_date"
          required
        />
        <Field
          id="end_date"
          caption="End Date"
          type="date"
          value={newService.end_date}
          handleChange={handleAddServiceChange}
          fieldId="end_date"
        />
      </AddModal>
    </div>
  );
};

export default Services;
